/* eslint-disable react-hooks/exhaustive-deps */
import Core from '../Core';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { IJsonSheet } from 'json-as-xlsx';
import { useAuth } from '../../hooks/useAuth';
import Pagination from '../Pricing/Pagination';
import { CompanyProps } from '../Company/types';
import { MdSimCardDownload } from 'react-icons/md';
import useWindowSize from '../../hooks/useWindowSize';
import { DefaultMessage } from '../../utils/defaultMessages';
import React, { useEffect, useMemo, useState } from 'react';
import ListItemValues from '../../components/History/ListItemValues';
import ListItemNotValues from '../../components/History/ListItemNotValues';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  List,
  TextField,
  Typography,
} from '@mui/material';
import ProductChipsHistories from '../../utils/ProductChips';
import CustomSelect from '../../components/CustomSelect';
import { LoggerProps } from './types';
import DownloadXLSX from '../../utils/DownloadXLSX/DownloadXLSX';

const now = new Date().toISOString().split('T')[0];
const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
  .toISOString()
  .split('T')[0];

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Histories(): JSX.Element {
  const { user } = useAuth();
  const { width } = useWindowSize();
  const [, setExcelData] = useState<IJsonSheet[]>();
  const [valueSearch, setValueSearch] = useState<string>('');
  const [pageSize, setPageSize] = useState<string>('10');
  const [companies, setCompanies] = useState<CompanyProps[]>();
  const [data, setData] = useState<undefined | LoggerProps[]>();
  const [indexSelected, setIndexSelected] = useState<number>(1);
  // const [productsIncluded, setProductsIncluded] = useState<string[]>([]);
  const [dataSearch, setDataSearch] = useState<undefined | any>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, settotalPages] = useState<number>(1);
  const [results, setResults] = useState<number>();
  const [disabledButtonXlsx, setDisabledButtonXlsx] = useState<boolean>(true);

  const productOptions = useMemo(() => {
    return ProductChipsHistories
      .filter((p) =>
        p.id !== 'all' && user?.products?.includes(p.id)
      )
      .map((p) => ({
        id: p.id,
        label: p.label,
      }));
  }, [user]);

  const onPageChange = (newPage: number) => {
    if (newPage !== currentPage) {
      setIndexSelected(0);
      setCurrentPage(newPage);
      setDataSearch([]);
      setValueSearch('');
      getLogs(newPage);
    }
  };

  const todayDate = getTodayDate();

  const { handleChange, handleSubmit, values, setFieldValue, errors, touched, setFieldError, setFieldTouched } = useFormik({
    initialValues: {
      initialDate: thirtyDaysAgo,
      finalDate: now,
      page: pageSize,
      company: [] as string[],
      product: productOptions.map((opt) => opt.id),
      query_field: 'none',
      query_value: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (values.query_field !== 'none' && !values.query_value) {
        errors.query_value = `Por favor, informe ${getQueryFieldLabel(values.query_field)}`;
      }

      const patternSomosCode = /^\d{6}-\d$/;
      if (values.query_field === 'somos_code' && !patternSomosCode.test(values.query_value)) {
        errors.query_value = `Código inválido. Use o formato 123456-1`;
      }

      const patternYearModel = /^\d{4}$/;
      if (values.query_field === 'year_model' && !patternYearModel.test(values.query_value)) {
        errors.query_value = `Ano modelo inválido. Use o formato xxxx (ex: 2025)`;
      }

      return errors;
    },
    onSubmit: () => getLogs(currentPage),
  });

  useEffect(() => {
    if (!valueSearch) {
      setDataSearch(data);
      return;
    }
    const search = valueSearch.toLowerCase();

    const filtered = data?.filter((item: any) => {
      const response = item?.response;

      if ('values' in response) {
        return response.values.some((subItem: any) => {
          return (
            subItem?.plate?.toLowerCase().includes(search) ||
            subItem?.model?.toLowerCase().includes(search) ||
            subItem?.chassi?.toLowerCase().includes(search) ||
            subItem?.mounter?.toLowerCase().includes(search)
          );
        });
      } else {
        return (
          response?.plate?.toLowerCase().includes(search) ||
          response?.model?.toLowerCase().includes(search) ||
          response?.chassi?.toLowerCase().includes(search) ||
          response?.mounter?.toLowerCase().includes(search)
        );
      }
    });
    setDataSearch(filtered);
  }, [valueSearch, data]);

  useEffect(() => {
    const allProductIds = productOptions.map((opt) => opt.id);
    setFieldValue('product', allProductIds);
  }, []);

  useEffect(() => {
    if (user?.is_staff || user?.is_admin) {
      getCompanies();
    }
  }, []);

  useEffect(() => {
    if (companies?.length) {
      const allCompanyIds = companies.map((c) => c.id);
      setFieldValue('company', allCompanyIds);
    }
  }, [companies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [values.initialDate, values.finalDate, values.company, values.product, pageSize]);

  useEffect(() => {
    setDisabledButtonXlsx(true)
  }, [values.initialDate, values.finalDate, values.company]);

  useEffect(() => {
    getLogs(currentPage)
  }, [])

  async function getLogs(page: number = 1) {
    setDataSearch(undefined);
    setExcelData(undefined);
    setIndexSelected(0);

    let url = `/table/history/?initial_date=${values.initialDate}&final_date=${values.finalDate}&page=${page}&page_size=${pageSize}`;
    const allCompanyIds = companies?.map((c) => c.id) ?? [];
    const isAllCompaniesSelected =
      values.company.length === allCompanyIds.length &&
      allCompanyIds.every((id) => values.company.includes(id));

    if (!isAllCompaniesSelected && values.company.length === 1) {
      url += `&company=${values.company[0]}`;
    }

    if (values.product.length > 0) {
      const productsQuery = values.product.map((id) => `product=${id}`).join('&');
      url += `&${productsQuery}`;
    }

    if (values.query_field && values.query_field !== 'none' && values.query_value) {
      url += `&query_field=${encodeURIComponent(values.query_field)}&query_value=${encodeURIComponent(values.query_value)}`;
    }

    await api
      .get(url)
      .then((response: any) => {
        setTimeout(() => {
          toast.dismiss();
          if (response.data.content.results.length === 0) {
            setDisabledButtonXlsx(true)
          } else {
            setDisabledButtonXlsx(false)
          }
          setData(response.data.content.results);
          settotalPages(response.data.content.total_pages);
          setResults(response.data.content.total_results);
          setDataSearch(response.data.content.results);
          setExcelData(response.data.content.xlsx);
        }, 2000);
      })
      .catch(() => {
        toast.error(DefaultMessage.ERROR, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }

  const getCompanies = async () => {
    await api
      .get('company/list/')
      .then((response: any) => {
        setCompanies(response.data.content);
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(DefaultMessage.ERROR, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const downloadXLSX = async () => {
    if (!values.initialDate || !values.finalDate) {
      toast.dismiss();
      toast.warning('Favor definir a data inicial e final!');
      return;
    }

    const allCompanyIds = companies?.map((c) => c.id) ?? [];
    const isAllCompaniesSelected =
      values.company.length === allCompanyIds.length &&
      allCompanyIds.every((id) => values.company.includes(id));

    let xlsxFileRoute = `/table/history-log-excel/?initial_date=${values.initialDate}&final_date=${values.finalDate}`;

    if (!isAllCompaniesSelected && values.company.length === 1) {
      xlsxFileRoute += `&company=${values.company[0]}`;
    }

    if (values.product.length > 0) {
      const productsQuery = values.product.map((id) => `product=${id}`).join('&');
      xlsxFileRoute += `&${productsQuery}`;
    }

    if (values.query_field && values.query_field !== 'none' && values.query_value) {
      xlsxFileRoute += `&query_field=${encodeURIComponent(values.query_field)}&query_value=${encodeURIComponent(values.query_value)}`;
    }

    DownloadXLSX(xlsxFileRoute).then().catch((error) => {
      if (error.status === 400) {
        setDisabledButtonXlsx(true)
      }
    })
  };

  const handleChangeCompany = (event: any) => {
    const selected = event.target.value as string;

    const allCompanyIds = companies?.map((company) => company.id) ?? [];
    const clickedAll = selected === 'all-company';

    if (clickedAll) {
      setFieldValue('company', allCompanyIds);
    } else {
      setFieldValue('company', [selected]);
    }
  };

  const handleChangePageSize = (event: any) => {
    const selected = event.target.value as string;
    setPageSize(selected)
    setFieldValue('page', selected)
  };

  const handleChangeSearchField = (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string;
    setFieldValue('query_field', value);

    setFieldValue('query_value', '');
    setFieldError('query_value', undefined);
    setFieldTouched('query_value', false, false);

  };

  const handleChangeProduct = (event: any) => {
    const selected = event.target.value as string[];
    const allIds = productOptions.map((opt) => opt.id);

    const clickedAll = selected.includes('all-products');

    const isCurrentlyAllSelected =
      (values.product as string[]).length === allIds.length &&
      allIds.every((id) => (values.product as string[]).includes(id));

    if (clickedAll) {
      if (isCurrentlyAllSelected) {
        setFieldValue('product', []);
      } else {
        setFieldValue('product', allIds);
      }
    } else {
      setFieldValue('product', selected);
    }
  };

  const optionsPageSize = [
    { id: '10', label: '10' },
    { id: '25', label: '25' },
    { id: '50', label: '50' },
    { id: '100', label: '100' },
  ];

  const isOnlyCaminhoesOnibus = values.product.length === 1 && values.product.includes('5')

  const query_fieldOptions = [
    { id: 'none', label: 'Nenhuma pesquisa' },
    { id: 'mounter', label: 'Marca' },
    { id: 'model', label: 'Modelo' },
    { id: 'year_model', label: 'Ano modelo' },
    ...(!isOnlyCaminhoesOnibus
      ? [{ id: 'somos_code', label: 'Cod somos' }]
      : []),
  ];

  const getQueryFieldLabel = (fieldId: string) => {
    const option = query_fieldOptions.find((opt) => opt.id === fieldId);
    return option ? option.label : '';
  };

  return (
    <Core withNavbar withBlueBar>
      <Grid
        container
        direction="column"
        gap={2}
        textAlign="center"
        paddingY={2}
      >
        <Typography variant="h5">
          <Chip
            label="RELATÓRIO DE CONSULTAS"
            style={{ width: '300px', height: '3rem', borderRadius: '50rem' }}
          />
        </Typography>
        <form className='md:flex md:w-full md:justify-center md:items-center md:flex-col' onSubmit={handleSubmit}>
          <Grid
            container
            gap={2}
            textAlign="center"
            justifyContent="space-around"
            width={width > 768 ? '80%' : '100%'}
          >
            <Grid item minWidth={'203.069px'} width={'300px'}>
              <Typography variant="body1">Data inicial</Typography>
              <TextField
                type="date"
                id="initialDate"
                name="initialDate"
                value={values.initialDate}
                onChange={handleChange}
                style={{ width: '300px' }}
                inputProps={{
                  max: todayDate,
                }}
              />
            </Grid>
            <Grid item minWidth={'203.069px'} width={'300px'}>
              <Typography variant="body1">Data final</Typography>
              <TextField
                type="date"
                id="finalDate"
                name="finalDate"
                value={values.finalDate}
                onChange={handleChange}
                style={{ width: '300px' }}
                inputProps={{
                  max: todayDate,
                }}
              />
            </Grid>
            <CustomSelect
              label="Quantidade por página"
              name="page"
              value={values.page}
              onChange={(event) => handleChangePageSize(event)}
              options={optionsPageSize}
              showAllOption={false}
            />
            {user?.is_staff || user?.is_admin ? (
              <CustomSelect
                label="Empresas"
                name="company"
                value={values.company}
                onChange={(event) => handleChangeCompany(event)}
                options={companies?.map((c) => ({ id: c.id, label: c.social_reason })) ?? []}
                allLabel="Todas as empresas"
                allValue="all-company"
              />
            ) : (
              <></>
            )}
            <Grid item minWidth={'203.069px'} width={'300px'}>
              <CustomSelect
                label="Produtos"
                name="product"
                value={values.product}
                onChange={(event) => handleChangeProduct(event)}
                options={productOptions}
                multiple
                allLabel="Todos os produtos"
                allValue="all-products"
              />
            </Grid>
            <Grid item minWidth={'203px'} width={'300px'}>
              <CustomSelect
                label="Pesquisar?"
                name="query_field"
                value={values.query_field}
                onChange={(e: any) => handleChangeSearchField(e)}
                options={query_fieldOptions}
                multiple={false}
                showAllOption={false}
              />
            </Grid>
            {values.query_field && values.query_field !== 'none' && (
              <Grid item minWidth={'203px'} width={'300px'}>
                <Grid item minWidth={'203.069px'} width={'300px'}>
                  <Typography variant="body1">{`Informe ${getQueryFieldLabel(values.query_field)}`}</Typography>
                  <TextField
                    type="text"
                    id="query_value"
                    name="query_value"
                    value={values.query_value}
                    onChange={handleChange}
                    style={{ width: '300px' }}
                    error={Boolean(errors.query_value && touched.query_value)}
                    helperText={errors.query_value && touched.query_value ? errors.query_value : ''}
                    inputProps={{
                      style: {
                        textAlign: 'center',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid
            container
            gap={2}
            textAlign="center"
            justifyContent="space-around"
            marginTop={2}
          >
            <Grid item width={'300px'}>
              <Button
                variant="contained"
                type="submit"
                style={{
                  width: '100%',
                  borderRadius: '50rem',
                  height: '2.5rem',
                  color: 'white',
                  background: '#00B9F2',
                }}
              >
                Consultar
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid paddingX={2}>
          <List
            sx={{ width: '100%', bgcolor: 'background.paper' }}
            component="div"
            subheader={
              <Typography variant="h6" color="text.primary">
                <Grid
                  container
                  flexDirection={'column'}
                  alignContent={'center'}
                  margin={'0'}
                >
                  <Chip
                    avatar={<ContentPasteSearchIcon />}
                    label={<strong>{results} resultados encontrados</strong>}
                    style={{
                      width: '300px',
                      borderRadius: '2rem',
                      height: '2.5rem',
                    }}
                  />
                  <Button
                    style={{
                      width: '300px',
                      color: 'white',
                      borderRadius: '2rem',
                      margin: '16px 0 2rem 0',
                      height: '2.5rem',
                    }}
                    sx={{
                      '&.Mui-disabled': {
                        background: '#bdbdbd',
                        color: '#fff',
                      },
                    }}
                    variant="contained"
                    onClick={downloadXLSX}
                    id="xlsxBtn"
                    disabled={disabledButtonXlsx}
                  >
                    {<MdSimCardDownload size={20} />} &nbsp; Baixar xlsx
                  </Button>
                </Grid>
              </Typography>
            }
          >
            {dataSearch?.length !== undefined && dataSearch.length > 0 ? (
              <>
                {dataSearch.map((mainItem: any, index: any) => {
                  if ('values' in mainItem.response) {
                    if (mainItem.response.pricing) {
                      return (
                        <ListItemValues
                          hash={mainItem.response.hash}
                          index={index}
                          key={`${index}-datasearch`}
                          itemType={mainItem.response.values[0].type}
                          indexSelected={indexSelected}
                          plate={mainItem.response.pricing.plate}
                          chassi={mainItem.response.pricing.chassi}
                          email={mainItem?.user?.email}
                          created_at={mainItem.created_at}
                          items={mainItem.response.values}
                          setIndexSelected={setIndexSelected}
                          pricing={true}
                        />
                      );
                    } else {
                      return (
                        <ListItemValues
                          index={index}
                          key={`${index}-datasearch`}
                          itemType={mainItem.response.values[0].type}
                          indexSelected={indexSelected}
                          email={mainItem?.user?.email}
                          model={
                            mainItem.response.values.length === 1
                              ? mainItem.response.values[0].model
                              : undefined
                          }
                          created_at={mainItem.created_at}
                          items={mainItem.response.values}
                          hash={mainItem.response.hash}
                          setIndexSelected={setIndexSelected}
                          pricing={false}
                        />
                      );
                    }
                  }
                  return (
                    <ListItemNotValues
                      index={index}
                      hash={mainItem.response.hash}
                      key={`${index}-datasearch`}
                      indexSelected={indexSelected}
                      email={mainItem?.user?.email}
                      item={mainItem.response}
                      created_at={mainItem.created_at}
                      setIndexSelected={setIndexSelected}
                    />
                  );
                })}
                <div className='mt-8'>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                  />
                </div>
              </>
            ) : dataSearch !== undefined ? (
              <Alert variant="standard" color="error">
                Nada encontrado para o período e cliente informado
              </Alert>
            ) : (
              <CircularProgress />
            )}
          </List>
        </Grid>
      </Grid>
    </Core>
  );
}